import Select, {
    ActionMeta,
    MultiValue,
    SingleValue,
    StylesConfig,
} from "react-select";
import CreatableSelect from "react-select/creatable";
import { toast } from "react-toastify";
import CustomToast from "./CustomToast";

export interface OptionType {
    label: string;
    value: string;
}

interface CustomSelectProps<IsMulti extends boolean = false> {
    id: string;
    label?: string;
    options: OptionType[];
    value?: IsMulti extends true
        ? MultiValue<OptionType>
        : SingleValue<OptionType>;
    onChange: (
        value: IsMulti extends true
            ? MultiValue<OptionType>
            : SingleValue<OptionType>,
        actionMeta: ActionMeta<OptionType>
    ) => void;
    placeholder?: string;
    isClearable?: boolean;
    className?: string;
    isMulti?: IsMulti;
    bg?: string;
    errorMessage?: string | null;
    isCreatable?: boolean;
    onCreateOption?: (inputValue: string) => void;
    maxSelectedOptions?: number; // Optional prop to limit the number of selected options (if needed)
    isDisabled?: boolean;
    isSearchable?: boolean;
    /** Set to "fixed" for dropdowns inside dialogs/modals to overlay content instead of pushing it */
    menuPosition?: "absolute" | "fixed";
    /** Portal target for the dropdown menu — pass document.body to escape stacking contexts */
    menuPortalTarget?: HTMLElement | null;
}

const CustomSelect = <IsMulti extends boolean = false>({
    id,
    label,
    options,
    value,
    onChange,
    placeholder = "Select...",
    isClearable = true,
    className,
    isMulti = false as IsMulti,
    bg,
    errorMessage = null,
    isCreatable = false,
    onCreateOption,
    maxSelectedOptions,
    isDisabled,
    isSearchable,
    menuPosition,
    menuPortalTarget,
}: CustomSelectProps<IsMulti>) => {
    const customSelectStyles: StylesConfig<OptionType, boolean> = {
        control: (base, state) => ({
            ...base,
            backgroundColor: bg || "#FFFFFF",
            borderColor: state.isFocused ? "#E9C468" : "#E5E7EB",
            borderWidth: 1,
            borderRadius: 8,
            boxShadow: "none",
            "&:hover": {
                borderColor: "none",
            },
            minHeight: "36px",
            height: "36px",
        }),
        valueContainer: (base) => ({
            ...base,
            height: "36px",
            padding: "0 12px",
        }),
        input: (base) => ({
            ...base,
            margin: "0px",
            padding: "0px",
            height: "36px",
        }),
        indicatorsContainer: (base) => ({
            ...base,
            height: "36px",
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected
                ? "#193456"
                : state.isFocused
                ? "#E9C468"
                : "#FFFFFF",
            color: state.isSelected ? "#FFFFFF" : "#193456",
            "&:hover": {
                backgroundColor: "#F0DFAF",
                color: "#193456",
            },
        }),
        menu: (base) => ({
            ...base,
            borderRadius: 8,
            marginTop: 5,
            boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
            // When menuPosition is provided, let react-select handle positioning
            // and ensure it renders above surrounding content.
            // Fallback to "relative" (legacy behaviour) when not set.
            ...(menuPosition
                ? { zIndex: 9999 }
                : { position: "relative" as const }),
        }),
        menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
        }),
        singleValue: (base) => ({
            ...base,
            color: "#193456",
            fontSize: "14px",
        }),
        placeholder: (base) => ({
            ...base,
            color: "#193456",
            fontSize: "14px",
        }),
        indicatorSeparator: () => ({ display: "none" }),
        dropdownIndicator: (base, state) => ({
            ...base,
            color: "#848484",
            opacity: 50,
            padding: "12px",
        }),
    };

    const handleChange = (
        option: IsMulti extends true
            ? MultiValue<OptionType>
            : SingleValue<OptionType>,
        actionMeta: ActionMeta<OptionType>
    ) => {
        if (
            isMulti &&
            maxSelectedOptions &&
            Array.isArray(option) &&
            option.length > maxSelectedOptions
        ) {
            if (option.length > maxSelectedOptions) {
                toast.error(
                    <CustomToast
                        message={`You can only select up to ${maxSelectedOptions} options.`}
                        type="error"
                    />,
                    {
                        autoClose: 3000,
                    }
                );
            }
            return;
        }
        onChange(option, actionMeta);
    };

    const SelectComponent = isCreatable ? CreatableSelect : Select;

    return (
        <div className={className}>
            {label && (
                <label className="block text-sm font-medium text-primary">
                    {label}
                </label>
            )}
            <SelectComponent<OptionType, IsMulti>
                id={id}
                options={options}
                value={value}
                onChange={handleChange}
                placeholder={placeholder}
                styles={customSelectStyles}
                isClearable={isClearable}
                isMulti={isMulti}
                {...(isCreatable && onCreateOption ? { onCreateOption } : {})}
                isDisabled={isDisabled}
                isSearchable={isSearchable ?? true}
                {...(menuPosition ? { menuPosition } : {})}
                {...(menuPortalTarget !== undefined ? { menuPortalTarget } : {})}
            />
            {errorMessage && (
                <p
                    id={`${id}-error`}
                    className="mt-1 text-sm text-red-600"
                    role="alert"
                >
                    {errorMessage}
                </p>
            )}
        </div>
    );
};

export default CustomSelect;
